import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Colors,
    ConfirmDialog,
    Icon,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';

import { normalizeTemplateQuestionScoring } from './normalizeTemplateQuestionScoring';
import { hasActiveQuestions, willSkillScopeProduceQuestions } from './previewGuard';

import type { QuestionTypeValue } from '@/constants/QuestionType';
import type { QuestionActionItemsProps } from '@/pages/shared/QuestionnaireCreation/types';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { ReviewTemplate } from '@/types/review/ReviewTemplate';
import type { DraggableItem, OptionProps } from '@amelio/ui-library';

import {
    useCreateReviewQuestion,
    useDeleteReviewQuestion,
    useUpdateReviewQuestion,
} from '@/api/review-services/review-question';
import {
    useDeleteReviewTemplate,
    useGetReviewTemplate,
    useUpdateReviewTemplate,
} from '@/api/review-services/review-template';
import { DraftSavedIndicator } from '@/components/DraftSavedIndicator/DraftSavedIndicator';
import { NotFoundState } from '@/components/NotFoundState/NotFoundState';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import {
    QuestionnaireFilters,
    type AdvancedOptionFilterType,
} from '@/constants/questionnaire/Filters';
import {
    ReviewQuestionStatus,
    type ReviewQuestionStatusType,
} from '@/constants/questionnaire/Question';
import {
    ManagerAssessmentSkillSelectorOption,
    type ManagerAssessmentSkillSelectorOptionValue,
} from '@/constants/review/ManagerAssessmentSkillSelectorOption';
import { ReviewTemplateStatus } from '@/constants/review/ReviewTemplateStatus';
import { useAmelioTranslations, useNavigate, useNavigateInNewPage } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useToggleQuestionStatus } from '@/hooks/useToggleQuestionStatus';
import { useError } from '@/hooks/utils/useError';
import { RouteName } from '@/navigation/types';
import { ManagerSkillSelectorSection } from '@/pages/ManageTemplates/TemplateQuestionnaire/components/ManagerSkillSelectorSection';
import {
    normalizeManagerSkillSelectorOption,
    useSkillSelectorPersistence,
} from '@/pages/ManageTemplates/TemplateQuestionnaire/useSkillSelectorPersistence';
import QuestionnaireCreation from '@/pages/shared/QuestionnaireCreation/components/QuestionnaireCreation';
import {
    createReviewTemplateSchema,
    questionSchema,
    type ReviewTemplateFormData,
} from '@/pages/shared/QuestionnaireCreation/validation';
import { showSuccessToast, showWarningToast } from '@/plugins/Toast';
import { getLocalizedText } from '@/utils/localization';
import { createEmptyQuestion, filterQuestions } from '@/utils/question';
import { validationMessageKey } from '@/utils/validationMessage';

export const TemplateQuestionnaireCreationPage = () => {
    const { t } = useAmelioTranslations();
    const defaultLanguage = useSelectedAppLanguage();
    const navigate = useNavigate();
    const navigateInNewPage = useNavigateInNewPage();
    const { handleError } = useError();
    const [questionToDelete, setQuestionToDelete] = useState<ReviewQuestion | undefined>(undefined);
    const [templateToDelete, setTemplateToDelete] = useState<ReviewTemplate | undefined>(undefined);
    const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const [questionTypeFilters, setQuestionTypeFilters] = useState<QuestionTypeValue[]>([]);
    const [advancedOptionsFilters, setAdvancedOptionsFilters] = useState<
        AdvancedOptionFilterType[]
    >([]);
    const location = useLocation<{ templateId?: string }>();
    const { templateId: templateIdFromRoute } = useParams<{ templateId?: string }>();

    const templateId = templateIdFromRoute || location.state?.templateId || '';

    const { mutateAsync: createQuestion, isPending: isPendingCreateQuestion } =
        useCreateReviewQuestion();
    const { mutateAsync: updateQuestion, isPending: isPendingUpdateQuestion } =
        useUpdateReviewQuestion();
    const { mutateAsync: deleteQuestion, isPending: isPendingDeleteQuestion } =
        useDeleteReviewQuestion();

    const { mutateAsync: updateQuestionnaire, isPending: isPendingUpdateQuestionnaire } =
        useUpdateReviewTemplate();
    const { mutateAsync: deleteTemplate, isPending: isDeletingTemplate } =
        useDeleteReviewTemplate();

    const {
        data: templateQuestionnaire,
        isLoading: isLoadingQuestionnaire,
        isError: isTemplateError,
    } = useGetReviewTemplate(templateId);

    const lastSavedAt = templateQuestionnaire?.updatedAt
        ? new Date(templateQuestionnaire.updatedAt)
        : null;

    const isInitialLoadRef = useRef(false);

    const reviewTemplateSchema = useMemo(
        () => createReviewTemplateSchema(defaultLanguage),
        [defaultLanguage],
    );

    const { watch, setValue, reset } = useForm<ReviewTemplateFormData>({
        resolver: zodResolver(reviewTemplateSchema),
        defaultValues: {
            id: '',
            title: {},
            reviewType: EvaluationType.EVALUATION_360,
            skillSelectorOption: null,
            questions: [],
        },
    });

    const formQuestions = watch('questions') || [];
    const formTitle = watch('title');
    const formReviewType = watch('reviewType');
    const formSkillSelectorOption = watch('skillSelectorOption');
    const normalizedSkillSelectorOption = useMemo(
        () => normalizeManagerSkillSelectorOption(formSkillSelectorOption),
        [formSkillSelectorOption],
    );

    const onSkillSelectorOptionChange = useCallback(
        (option: ManagerAssessmentSkillSelectorOptionValue) =>
            setValue('skillSelectorOption', option, { shouldDirty: true }),
        [setValue],
    );

    const { onSkillSelectorChange, ensureOptionPersisted } = useSkillSelectorPersistence({
        template: templateQuestionnaire,
        title: formTitle,
        questions: formQuestions,
        selectedOption: normalizedSkillSelectorOption,
        updateTemplate: updateQuestionnaire,
        onOptionChange: onSkillSelectorOptionChange,
    });

    const [isAdding, setIsAdding] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
    const questionSnapshotRef = useRef<ReviewQuestion | null>(null);
    const [tempQuestion, setTempQuestion] = useState<ReviewQuestion>(() =>
        createEmptyQuestion(defaultLanguage),
    );

    const currentQuestion = useMemo(() => {
        if (selectedQuestionId) return formQuestions.find((q) => q.id === selectedQuestionId);
        return tempQuestion;
    }, [selectedQuestionId, formQuestions, tempQuestion]);

    const onCreateQuestion = async (question: ReviewQuestion) => {
        const scoredQuestion = normalizeTemplateQuestionScoring({
            question,
            reviewType: formReviewType,
        });
        const result = questionSchema.safeParse(scoredQuestion);

        if (!result.success) {
            const errorMessage = validationMessageKey(result.error.issues[0]?.message);
            showWarningToast({ message: t(errorMessage) });
            return;
        }
        try {
            const response = await createQuestion({
                ...result.data,
                reviewTemplateId: templateId,
                sortOrder: formQuestions.length,
            });
            setValue('questions', [...formQuestions, { ...scoredQuestion, id: response.id }]);
            setIsAdding(false);
            setTempQuestion(createEmptyQuestion(defaultLanguage));

            showSuccessToast({ message: t('evaluation360Questionnaire.message.questionCreated') });
        } catch (error) {
            handleError({ error });
        }
    };

    const { onToggleQuestionStatus: toggleQuestionStatus } = useToggleQuestionStatus({
        updateQuestion,
        formQuestions,
        onSuccess: (updatedQuestions) => setValue('questions', updatedQuestions),
    });

    const onToggleQuestionStatus = useCallback(
        async (question: ReviewQuestion, newStatus: ReviewQuestionStatusType) => {
            const removesLastActiveQuestion =
                newStatus === ReviewQuestionStatus.INACTIVE &&
                question.status === ReviewQuestionStatus.ACTIVE &&
                !formQuestions.some(
                    (q) => q.id !== question.id && q.status === ReviewQuestionStatus.ACTIVE,
                );

            if (removesLastActiveQuestion) {
                if (
                    normalizedSkillSelectorOption ===
                    ManagerAssessmentSkillSelectorOption.DO_NOT_ASSESS_SKILLS
                ) {
                    showWarningToast({
                        message: t(
                            'templateManagement.template.error.cannotDeactivateLastActiveQuestionWithDoNotAssessSkills',
                        ),
                    });
                    return;
                }

                if (!(await ensureOptionPersisted())) return;
            }

            return toggleQuestionStatus(question, newStatus);
        },
        [
            toggleQuestionStatus,
            formQuestions,
            normalizedSkillSelectorOption,
            ensureOptionPersisted,
            t,
        ],
    );

    const questionList = useMemo(
        () => filterQuestions({ formQuestions, questionTypeFilters, advancedOptionsFilters }),
        [formQuestions, questionTypeFilters, advancedOptionsFilters],
    );

    const onUpdateQuestion = useCallback(
        async (question: ReviewQuestion) => {
            const scoredQuestion = normalizeTemplateQuestionScoring({
                question,
                reviewType: formReviewType,
            });
            const result = questionSchema.safeParse(scoredQuestion);

            if (!result.success) {
                const errorMessage = validationMessageKey(result.error.issues[0]?.message);
                showWarningToast({ message: t(errorMessage) });
                return;
            }
            try {
                await updateQuestion(result.data);
                const updatedQuestions = formQuestions.map((q) =>
                    q.id === scoredQuestion.id ? scoredQuestion : q,
                );
                setValue('questions', updatedQuestions);
                setSelectedQuestionId(null);

                showSuccessToast({
                    message: t('evaluation360Questionnaire.message.questionUpdated'),
                });
            } catch (error) {
                handleError({ error });
            }
        },
        [updateQuestion, formQuestions, formReviewType, setValue, handleError, t],
    );

    const onDeleteQuestion = useCallback((question: ReviewQuestion) => {
        if (!question.id) {
            return;
        }

        setQuestionToDelete(question);
    }, []);

    const onConfirmDeleteQuestion = useCallback(async () => {
        if (!questionToDelete?.id) {
            return;
        }

        const remainingQuestions = formQuestions.filter((q) => q.id !== questionToDelete.id);
        const removesLastActiveQuestion =
            questionToDelete.status === ReviewQuestionStatus.ACTIVE &&
            !remainingQuestions.some((q) => q.status === ReviewQuestionStatus.ACTIVE);

        if (removesLastActiveQuestion) {
            if (
                normalizedSkillSelectorOption ===
                ManagerAssessmentSkillSelectorOption.DO_NOT_ASSESS_SKILLS
            ) {
                showWarningToast({
                    message: t(
                        'templateManagement.template.error.cannotDeleteLastActiveQuestionWithDoNotAssessSkills',
                    ),
                });
                setQuestionToDelete(undefined);
                return;
            }

            if (!(await ensureOptionPersisted())) {
                setQuestionToDelete(undefined);
                return;
            }
        }

        try {
            await deleteQuestion(questionToDelete.id);
            setValue('questions', remainingQuestions);

            showSuccessToast({
                message: t('evaluation360Questionnaire.message.questionDeleted'),
            });
        } catch (error) {
            handleError({ error });
        } finally {
            setQuestionToDelete(undefined);
        }
    }, [
        questionToDelete,
        deleteQuestion,
        formQuestions,
        setValue,
        handleError,
        t,
        normalizedSkillSelectorOption,
        ensureOptionPersisted,
    ]);

    const onConfirmDeleteTemplate = useCallback(async () => {
        if (!templateToDelete?.id) {
            return;
        }

        try {
            await deleteTemplate(templateToDelete.id);

            showSuccessToast({
                message: t('templateManagement.template.message.deleteSuccess'),
            });

            navigate({ path: RouteName.ManageTemplates });
        } catch (error) {
            handleError({ error });
        } finally {
            setTemplateToDelete(undefined);
        }
    }, [templateToDelete, deleteTemplate, handleError, t, navigate]);

    const onEditQuestion = useCallback(
        (question: ReviewQuestion) => {
            // First, restore any unsaved changes from the previously selected question
            if (selectedQuestionId && questionSnapshotRef.current) {
                const restoredQuestions = formQuestions.map((questionItem) =>
                    questionItem.id === selectedQuestionId
                        ? questionSnapshotRef.current!
                        : questionItem,
                );
                setValue('questions', restoredQuestions);
            }

            // Then, set up the new question for editing
            questionSnapshotRef.current = formQuestions.find((q) => q.id === question.id) ?? null;
            setSelectedQuestionId(question.id || null);
        },
        [formQuestions, selectedQuestionId, setValue],
    );

    const questionActionItems: QuestionActionItemsProps[] = [
        {
            icon: <Icon name={'edit-edit-2'} />,
            label: t('common.edit'),
            onClick: (question: ReviewQuestion) => onEditQuestion(question),
        },
        {
            icon: (
                <Icon
                    name={'essential-trash'}
                    color={Colors.ERROR}
                />
            ),
            label: t('common.delete'),
            danger: true,
            onClick: (question: ReviewQuestion) => onDeleteQuestion(question),
        },
    ];

    const templateCardActions = (template: ReviewTemplate | undefined): OptionProps[] => {
        if (!template) return [];
        return [
            {
                icon: (
                    <Icon
                        name={'essential-trash'}
                        color={Colors.ERROR}
                    />
                ),
                id: 'delete',
                label: t('common.delete'),
                danger: true,
            },
            {
                icon: (
                    <Icon
                        name={
                            template.status === ReviewTemplateStatus.ACTIVE
                                ? 'essential-close-circle'
                                : 'essential-checkmark'
                        }
                    />
                ),
                id: 'status',
                label:
                    template.status === ReviewTemplateStatus.ACTIVE
                        ? t('common.deactivate')
                        : t('common.activate'),
            },
        ];
    };

    const onTemplateActionClick = (actionId: string) => {
        if (actionId === 'delete') {
            // Handle delete
            if (templateQuestionnaire) {
                setTemplateToDelete(templateQuestionnaire);
            }
        } else if (actionId === 'status') {
            // Handle status toggle
            if (templateQuestionnaire) {
                onStatusChange(templateQuestionnaire);
            }
        }
    };

    const onStatusChange = async (reviewTemplate: ReviewTemplate) => {
        try {
            await updateQuestionnaire({
                ...reviewTemplate,
                status:
                    reviewTemplate.status === ReviewTemplateStatus.ACTIVE
                        ? ReviewTemplateStatus.INACTIVE
                        : ReviewTemplateStatus.ACTIVE,
            });

            showSuccessToast({
                message: t(
                    reviewTemplate.status === ReviewTemplateStatus.ACTIVE
                        ? 'templateManagement.template.message.deactivateSuccess'
                        : 'templateManagement.template.message.activateSuccess',
                ),
            });
        } catch (error) {
            handleError({ error });
        }
    };

    const onQuestionChange = useCallback(
        (question: ReviewQuestion) => {
            if (selectedQuestionId) {
                const updatedQuestions = formQuestions.map((q) =>
                    q.id === selectedQuestionId ? question : q,
                );
                setValue('questions', updatedQuestions);
            } else {
                setTempQuestion(question);
            }
        },
        [formQuestions, selectedQuestionId, setValue],
    );

    const handleTitleActionButton = async () => {
        if (isEditingTitle) {
            const result = reviewTemplateSchema.safeParse({
                id: templateQuestionnaire?.id || '',
                title: formTitle,
                reviewType:
                    formReviewType ??
                    templateQuestionnaire?.reviewType ??
                    EvaluationType.EVALUATION_360,
                skillSelectorOption: normalizedSkillSelectorOption,
                questions: formQuestions,
            });

            if (!result.success) {
                const errorMessage = validationMessageKey(result.error.issues[0]?.message);
                showWarningToast({ message: t(errorMessage) });
                return;
            }

            try {
                await updateQuestionnaire({
                    ...templateQuestionnaire!,
                    title: formTitle ?? '',
                    description: templateQuestionnaire?.description ?? '',
                    skillSelectorOption: normalizedSkillSelectorOption,
                    questions: formQuestions,
                });
                onCancelEdit();
            } catch (error) {
                handleError({ error });
            }
            setIsEditingTitle(false);
        } else {
            setIsEditingTitle(true);
        }
    };

    const handleSaveAndGoBack = async () => {
        const result = reviewTemplateSchema.safeParse({
            id: templateQuestionnaire?.id || '',
            title: formTitle,
            reviewType:
                formReviewType ??
                templateQuestionnaire?.reviewType ??
                EvaluationType.EVALUATION_360,
            skillSelectorOption: normalizedSkillSelectorOption,
            questions: formQuestions,
        });
        if (!result.success) {
            const errorMessage = validationMessageKey(result.error.issues[0]?.message);
            showWarningToast({ message: t(errorMessage) });
            return;
        }

        try {
            const publishedStatus =
                templateQuestionnaire?.status === ReviewTemplateStatus.DRAFT
                    ? ReviewTemplateStatus.ACTIVE
                    : templateQuestionnaire?.status;
            await updateQuestionnaire({
                ...templateQuestionnaire!,
                title: formTitle ?? {},
                description: templateQuestionnaire?.description ?? '',
                skillSelectorOption: normalizedSkillSelectorOption,
                questions: formQuestions,
                status: publishedStatus ?? ReviewTemplateStatus.ACTIVE,
            });
            navigate({ path: RouteName.ManageTemplates });
        } catch (error) {
            handleError({ error });
        }
    };

    const onCancelEdit = useCallback(() => {
        if (selectedQuestionId && questionSnapshotRef.current) {
            const restoredQuestions = formQuestions.map((q) =>
                q.id === selectedQuestionId ? questionSnapshotRef.current! : q,
            );
            setValue('questions', restoredQuestions);
        }
        setIsAdding(false);
        setSelectedQuestionId(null);
        setTempQuestion(createEmptyQuestion(defaultLanguage));
        questionSnapshotRef.current = null;
    }, [defaultLanguage, selectedQuestionId, formQuestions, setValue]);

    const onReorderQuestions = useCallback(
        (items: DraggableItem<ReviewQuestion>[]) => {
            const reorderedQuestions = items.map((item, index) => ({
                ...item.data,
                sortOrder: index,
            }));

            setValue('questions', reorderedQuestions);

            updateQuestionnaire({
                ...templateQuestionnaire!,
                title: formTitle ?? '',
                description: templateQuestionnaire?.description ?? '',
                skillSelectorOption: normalizedSkillSelectorOption,
                questions: reorderedQuestions,
            });
        },
        [
            formTitle,
            normalizedSkillSelectorOption,
            setValue,
            templateQuestionnaire,
            updateQuestionnaire,
        ],
    );

    const handleFilterChange = (filter: string) => {
        const isSelected = selectedFilters.includes(filter);

        if (isSelected) {
            setSelectedFilters((prev) => prev.filter((f) => f !== filter));

            if (filter === QuestionnaireFilters.QUESTION_TYPE) {
                setQuestionTypeFilters([]);
            } else if (filter === QuestionnaireFilters.ADVANCED_OPTIONS) {
                setAdvancedOptionsFilters([]);
            }
        } else {
            setSelectedFilters((prev) => [...prev, filter]);
        }
    };

    const filterOptions: OptionProps[] = [
        {
            label: t('evaluation360Questionnaire.questionForm.filters.advancedOptions'),
            value: QuestionnaireFilters.ADVANCED_OPTIONS,
        },
        {
            label: t('evaluation360Questionnaire.questionForm.filters.questionType'),
            value: QuestionnaireFilters.QUESTION_TYPE,
        },
    ];

    const onQuestionTypeFilterChange = (filter: QuestionTypeValue) => {
        setQuestionTypeFilters((prev) => {
            if (prev.includes(filter)) {
                return prev.filter((f) => f !== filter);
            }

            return [...prev, filter];
        });
    };

    const onAdvancedOptionsFilterChange = (filter: AdvancedOptionFilterType) => {
        setAdvancedOptionsFilters((prev) => {
            if (prev.includes(filter)) {
                return prev.filter((f) => f !== filter);
            }

            return [...prev, filter];
        });
    };

    const onPreviewClick = () => {
        const hasDynamicCompetencyQuestions =
            formReviewType === EvaluationType.MANAGER_EVALUATION &&
            willSkillScopeProduceQuestions(normalizedSkillSelectorOption);
        const isPreviewBlocked =
            !hasDynamicCompetencyQuestions && !hasActiveQuestions(formQuestions);

        if (isPreviewBlocked) {
            showWarningToast({ message: t('evaluation360Questionnaire.message.noQuestions') });
            return;
        }

        navigateInNewPage({
            path: RouteName.SurveyAnswer,
            searchParams: {
                questionnaireId: templateQuestionnaire?.id,
                reviewType: templateQuestionnaire?.reviewType,
                isTemplate: 'true',
            },
        });
    };

    const questionnaireTopContent =
        templateQuestionnaire?.reviewType === EvaluationType.MANAGER_EVALUATION ? (
            <ManagerSkillSelectorSection
                value={normalizedSkillSelectorOption ?? undefined}
                onChange={onSkillSelectorChange}
                isDisabled={isPendingUpdateQuestionnaire}
            />
        ) : undefined;

    useEffect(() => {
        const isMutationPending =
            isPendingCreateQuestion ||
            isPendingUpdateQuestion ||
            isPendingDeleteQuestion ||
            isPendingUpdateQuestionnaire;

        if (templateQuestionnaire && (!isInitialLoadRef.current || !isMutationPending)) {
            const isFirstLoad = !isInitialLoadRef.current;
            isInitialLoadRef.current = true;
            reset({
                id: templateQuestionnaire.id,
                title: templateQuestionnaire.title ?? {},
                reviewType: templateQuestionnaire.reviewType ?? EvaluationType.EVALUATION_360,
                skillSelectorOption: isFirstLoad
                    ? normalizeManagerSkillSelectorOption(templateQuestionnaire.skillSelectorOption)
                    : normalizedSkillSelectorOption,
                questions: templateQuestionnaire.questions || [],
            });
        }
    }, [
        templateQuestionnaire,
        reset,
        isPendingCreateQuestion,
        isPendingUpdateQuestion,
        isPendingDeleteQuestion,
        isPendingUpdateQuestionnaire,
    ]);

    if (isLoadingQuestionnaire) {
        return (
            <Skeleton className={'px-8'}>
                <Bone className={'h-15 w-50'} />

                <Bone className={'mt-20 flex h-50 w-150'} />

                <Bone className={'mt-10 flex h-500 w-full'} />
            </Skeleton>
        );
    }

    if (isTemplateError) {
        return (
            <NotFoundState
                titleKey={'errors.notFoundOrInaccessibleTitle'}
                messageKey={'errors.notFoundOrInaccessibleMessage'}
            />
        );
    }

    return (
        <div className={'flex h-full flex-col'}>
            <div className={'relative z-10 flex-1'}>
                <QuestionnaireCreation
                    isAddingQuestion={isAdding}
                    questions={questionList}
                    selectedQuestionId={selectedQuestionId}
                    currentQuestion={currentQuestion}
                    questionActionItems={questionActionItems}
                    onRowClick={onEditQuestion}
                    isEditingTitle={isEditingTitle}
                    title={getLocalizedText(formTitle)}
                    isPendingUpdateQuestion={isPendingUpdateQuestion}
                    isPendingCreateQuestion={isPendingCreateQuestion}
                    setIsEditingTitle={() => handleTitleActionButton()}
                    onCancelTitle={() => setIsEditingTitle(false)}
                    onTitleChange={(v: string) =>
                        setValue('title', { ...formTitle, [defaultLanguage]: v })
                    }
                    onAddQuestionClick={() => setIsAdding(true)}
                    onPreviewClick={onPreviewClick}
                    onCancelEdit={onCancelEdit}
                    onQuestionChange={onQuestionChange}
                    onReorderQuestions={onReorderQuestions}
                    onUpdateQuestion={onUpdateQuestion}
                    onCreateQuestion={onCreateQuestion}
                    onToggleQuestionStatus={onToggleQuestionStatus}
                    isTemplateCreationPage
                    isPendingUpdateQuestionnaire={isPendingUpdateQuestionnaire}
                    topContent={questionnaireTopContent}
                    bottomContent={
                        <div className={'mt-3 mb-2 flex justify-center'}>
                            <DraftSavedIndicator lastSavedAt={lastSavedAt} />
                        </div>
                    }
                    filterOptions={filterOptions}
                    handleFilterChange={handleFilterChange}
                    selectedFilters={selectedFilters}
                    advancedOptionsFilters={advancedOptionsFilters}
                    questionTypeFilters={questionTypeFilters}
                    onAdvancedOptionsFilterChange={onAdvancedOptionsFilterChange}
                    onQuestionTypeFilterChange={onQuestionTypeFilterChange}
                    onTranslateClick={() =>
                        navigate({
                            path: `${RouteName.TemplateTranslation}/${templateId}`,
                            state: { templateId },
                        })
                    }
                    reviewType={templateQuestionnaire?.reviewType}
                    skillSelectorOption={normalizedSkillSelectorOption ?? undefined}
                    templateStatus={templateQuestionnaire?.status}
                    templateActions={templateCardActions(templateQuestionnaire)}
                    onTemplateActionClick={onTemplateActionClick}
                />
            </div>
            <div
                className={
                    'flex flex-col items-start justify-between md:mt-2 md:flex-row md:items-end'
                }
            >
                <div className={'ml-auto flex flex-wrap gap-4'}>
                    <ButtonSecondary
                        className={'border border-gray-300'}
                        dataTestId={'back-button'}
                        onClick={() => navigate({ path: RouteName.ManageTemplates })}
                        leftIcon={
                            <Icon
                                name={'arrow-arrow-left'}
                                className={'mr-2'}
                            />
                        }
                    >
                        {t('common.back')}
                    </ButtonSecondary>

                    <ButtonGradientPrimary
                        dataTestId={'save-button'}
                        onClick={handleSaveAndGoBack}
                        leftIcon={
                            <Icon
                                name={'archive-save-add'}
                                className={'mr-2'}
                            />
                        }
                    >
                        {t('common.save')}
                    </ButtonGradientPrimary>
                </div>
            </div>
            <ConfirmDialog
                isOpen={Boolean(questionToDelete)}
                title={t('common.attention')}
                message={t('common.deleteQuestionMessage')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={onConfirmDeleteQuestion}
                onCancel={() => setQuestionToDelete(undefined)}
                isLoading={isPendingDeleteQuestion}
                isDestructive
            />

            <ConfirmDialog
                isOpen={Boolean(templateToDelete)}
                title={t('common.deleteConfirmTitle')}
                message={t('templateManagement.template.deleteConfirmMessage')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={onConfirmDeleteTemplate}
                onCancel={() => setTemplateToDelete(undefined)}
                isLoading={isDeletingTemplate}
                isDestructive
            />
        </div>
    );
};
